import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api, { apiErrorMessage, type InboxData, type InboxGroup, type InboxItem } from '../services/api';
import { formatCountdown, formatLessonTime } from '../utils/time';
import {
  IconGroup, IconClockLate, IconCloseX, IconPresentCheck, IconHomework, IconLocationPin,
  IconMoney, IconInbox, IconRefresh, IconCalendar, IconTrialStar, IconPhone, IconBook, IconSchool, IconLink,
} from '../components/Icons';

// ─── Emoji → inline SVG for summary bar ──────────────────────────────────
function renderSummaryIcon(icon: string, className = 'w-4 h-4') {
  const map: Record<string, React.ReactNode> = {
    '📚': <IconBook className={className} />,
    '💰': <IconMoney className={className} />,
    '🌟': <IconTrialStar className={className} />,
  };
  return map[icon] || <span className={className}>{icon}</span>;
}

// ─── Emoji → image file for group headers ────────────────────────────────
function renderGroupIcon(icon: string, className = 'w-4 h-4') {
  const map: Record<string, string> = {
    '📚': '/icons/Lesson-1.png',
    '💰': '/icons/payments.svg',
    '🌟': '/icons/Пробное.svg',
  };
  const src = map[icon];
  return src ? <img src={src} alt="" className={className} /> : <span className={className}>{icon}</span>;
}

const PRIORITY_STYLES: Record<string, { dot: string; bg: string; border: string; text: string }> = {
  high: { dot: 'bg-red-500', bg: 'bg-red-50', border: 'border-red-100', text: 'text-red-700' },
  medium: { dot: 'bg-amber-500', bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-700' },
  low: { dot: 'bg-gray-300', bg: 'bg-gray-50', border: 'border-gray-100', text: 'text-gray-500' },
};

function formatWeekday(dateStr: string): string {
  const d = new Date(dateStr);
  const weekdays = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
  return weekdays[d.getDay()];
}

// ─── Group Header ─────────────────────────────────────────────────────────

function GroupHeader({ group, collapsed, onToggle }: {
  group: InboxGroup;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const urgentCount = group.items.filter(i => i.priority === 'high').length;

  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-white shadow-sm transition-all duration-200 active:scale-[0.98]"
      style={{ backgroundColor: 'var(--tg-theme-button-color)' }}
    >
      <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-lg shrink-0">
        {renderGroupIcon(group.icon, 'w-5 h-5')}
      </div>
      <div className="flex-1 text-left">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{group.label}</span>
          <span className="text-[11px] font-medium bg-white/20 rounded-full px-2 py-0.5">
            {group.items.length}
          </span>
        </div>
        {urgentCount > 0 && !collapsed && (
          <span className="text-[10px] text-white/80">
            {urgentCount} {urgentCount === 1 ? 'требует' : 'требуют'} внимания
          </span>
        )}
      </div>
      <svg
        className={`w-4 h-4 text-white/70 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  );
}

// ─── Lesson Card (rich lesson display) ───────────────────────────────────

function LessonCard({ item }: { item: InboxItem }) {
  const navigate = useNavigate();
  const color = item.color || '#6C5CE7';
  const isHigh = item.priority === 'high';
  const isCurrent = item.lesson_status === 'current';
  const isPast = item.lesson_status === 'past';

  // Занятие с tz: показываем время и отсчёт в локальном поясе устройства.
  // Легаси (без tz): используем строки, пришедшие с сервера, как раньше.
  const hasTz = !!item.lesson_tz;
  const displayTime = hasTz
    ? (formatLessonTime({ date: item.lesson_date, start_time: item.lesson_start, end_time: item.lesson_end, tz: item.lesson_tz }) || item.lesson_time || '')
    : (item.lesson_time || '');
  const titleText = hasTz
    ? (item.title.replace(item.lesson_time || '', displayTime).trim() || item.title)
    : item.title;
  const countdown = hasTz
    ? formatCountdown(item.lesson_date || '', item.lesson_start || '', item.lesson_tz || '')
    : (item.time_until || '');

  return (
    <button
      onClick={() => navigate(item.action_url)}
      className="w-full text-left transition-all duration-150 active:scale-[0.97] group"
    >
      <div className="rounded-xl bg-white border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
        {/* Color accent bar */}
        <div className="h-1.5" style={{ backgroundColor: color }} />

        <div className="p-4">
          {/* Top row: time + time_until badge */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold" style={{ color }}>
                {displayTime}
              </span>
              <span className="text-sm font-semibold text-gray-800">
                {titleText}
              </span>
            </div>

            {/* Time-until badge */}
            {countdown && (
              <div className={`
                shrink-0 px-2.5 py-1 rounded-full text-[10px] font-semibold
                ${isCurrent
                  ? 'bg-green-100 text-green-700 animate-pulse'
                  : isPast
                    ? 'bg-gray-100 text-gray-400'
                    : 'bg-indigo-100 text-indigo-700'
                }
              `}>
                {isCurrent ? '🔴 ' : ''}{countdown}
              </div>
            )}
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-4 text-[12px] text-gray-500 mb-3">
            {item.student_count !== undefined && (
              <span className="flex items-center gap-1">
                <span>👥</span>
                <span>{item.student_count} {item.student_count === 1 ? 'ученик' : 'учеников'}</span>
              </span>
            )}
            <span className="flex items-center gap-1">
              <span className={`w-2 h-2 rounded-full shrink-0 ${isHigh ? 'bg-red-500' : isCurrent ? 'bg-amber-400' : 'bg-green-500'}`} />
              <span>{item.subtitle}</span>
            </span>
          </div>

          {/* Action button */}
          <div
            className={`
              w-full py-2 rounded-xl text-[12px] font-semibold text-center transition-all duration-150
              ${isHigh
                ? 'text-white shadow-sm'
                : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
              }
              group-hover:opacity-90
            `}
            style={isHigh ? { backgroundColor: color } : {}}
          >
            {item.action_label}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="inline ml-1 -mt-0.5">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── Inbox Card (generic for non-lesson groups) ──────────────────────────

function InboxCard({ item, onDismiss }: { item: InboxItem; onDismiss?: (id: string) => void }) {
  const navigate = useNavigate();
  const isHigh = item.priority === 'high';
  const styles = PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.low;

  return (
    <div className={`rounded-xl p-3.5 ${styles.bg} border ${styles.border} hover:shadow-sm transition-shadow group`}>
      <div className="flex items-center gap-3">
        {/* Priority dot */}
        <div className="flex flex-col items-center shrink-0">
          <div className={`w-2.5 h-2.5 rounded-full ${styles.dot} ${isHigh ? 'animate-pulse' : ''}`} />
        </div>

        {/* Content — кликабельно */}
        <button onClick={() => navigate(item.action_url)} className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-semibold ${styles.text} truncate`}>
              {item.title}
            </span>
            {isHigh && (
              <span className="text-[9px] font-bold text-red-500 bg-red-100 px-1.5 py-0.5 rounded-full shrink-0">
                !
              </span>
            )}
          </div>
          {item.subtitle && (
            <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-1">
              {item.subtitle}
            </p>
          )}
        </button>

        {/* Action chip + dismiss */}
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => navigate(item.action_url)}
            className={`px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all
              ${isHigh
                ? 'bg-red-500 text-white shadow-sm shadow-red-500/30'
                : `${styles.bg} ${styles.text} border ${styles.border}`
              }
              hover:opacity-80`}
          >
            {item.action_label}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="inline ml-0.5 -mt-0.5">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          {onDismiss && (
            <button
              onClick={(e) => { e.stopPropagation(); onDismiss(item.id); }}
              className="p-1 rounded-full hover:bg-white/50 text-gray-400 hover:text-gray-600 transition-all text-[10px]"
              title="Убрать из списка"
            >
              ✓
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Payment Card (for payments group) ─────────────────────────────────────

function PaymentCard({ item }: { item: InboxItem }) {
  const navigate = useNavigate();
  const isOverdue = item.priority === 'high';

  return (
    <button
      onClick={() => navigate(item.action_url)}
      className="w-full text-left transition-all active:scale-[0.98]"
    >
      <div className={`rounded-xl p-3.5 border-2 transition-all hover:shadow-md ${
        isOverdue
          ? 'bg-red-50 border-red-200'
          : 'bg-amber-50 border-amber-200'
      }`}>
        <div className="flex items-center gap-3">
          {/* Student avatar or alert icon */}
          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 overflow-hidden ${
            item.photo_url ? '' : isOverdue ? 'bg-red-100' : 'bg-amber-100'
          }`}>
            {item.photo_url ? (
              <img src={item.photo_url} alt="" className="w-full h-full object-cover" />
            ) : item.first_name ? (
              <span className={`text-xs font-bold ${isOverdue ? 'text-red-500' : 'text-amber-500'}`}>
                {item.first_name[0]}{item.last_name?.[0] || ''}
              </span>
            ) : isOverdue ? (
              <img src="/icons/close-x.svg" alt="" className="w-4 h-4" />
            ) : (
              <img src="/icons/clock-late.svg" alt="" className="w-4 h-4" />
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <span className={`text-sm font-semibold block truncate ${
              isOverdue ? 'text-red-800' : 'text-amber-800'
            }`}>
              {item.title}
            </span>
            <p className={`text-[11px] mt-0.5 ${
              isOverdue ? 'text-red-600' : 'text-amber-600'
            }`}>
              {item.subtitle}
            </p>
          </div>

          {/* Action button */}
          <div
            onClick={(e) => { e.stopPropagation(); navigate(item.action_url); }}
            className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all hover:opacity-80 ${
              isOverdue
                ? 'bg-red-500 text-white shadow-sm shadow-red-500/30'
                : 'bg-amber-500 text-white shadow-sm shadow-amber-500/30'
            }`}
          >
            {item.action_label}
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── Trial Card (for trials group) ────────────────────────────────────────

function TrialCard({ item }: { item: InboxItem }) {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate(item.action_url)}
      className="w-full text-left transition-all active:scale-[0.98]"
    >
      <div className="rounded-xl p-3.5 border border-violet-200 bg-violet-50 transition-all hover:shadow-md">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center shrink-0 overflow-hidden">
            {item.photo_url ? (
              <img src={item.photo_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs font-bold text-violet-600">
                {(item.first_name?.[0] || '?')}{item.last_name?.[0] || ''}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-semibold text-violet-800 block truncate">
              {item.title}
            </span>
            <p className="text-[11px] text-violet-600 mt-0.5">
              {item.subtitle}
            </p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(item.action_url); }}
            className="shrink-0 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-violet-500 text-white shadow-sm shadow-violet-500/30 transition-all hover:opacity-80"
          >
            {item.action_label}
          </button>
        </div>
      </div>
    </button>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────

export default function InboxPage() {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const { theme } = useTheme();
  const [inbox, setInbox] = useState<InboxData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const [localReminders, setLocalReminders] = useState<any[]>([]);
  const [dismissedCancelled, setDismissedCancelled] = useState<Set<string>>(new Set());

  // ── Quick actions modals ────────────────────────────────────────────
  const [showNewStudent, setShowNewStudent] = useState(false);
  const [showOneOffLesson, setShowOneOffLesson] = useState(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [courseGroups, setCourseGroups] = useState<any[]>([]);
  const [savingQuickAction, setSavingQuickAction] = useState(false);

  // New Student form
  const [newStudentForm, setNewStudentForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    course_id: '',
    group_id: '',
  });

  // One-off Lesson form
  const [oneOffLessonForm, setOneOffLessonForm] = useState({
    title: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    location_link: '',
  });

  useEffect(() => {
    loadInbox();
    const interval = setInterval(loadInbox, 30000);
    return () => clearInterval(interval);
  }, []);

  // Загружаем отклонённые отменённые занятия из localStorage
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('edu_pulse_dismissed_cancelled') || '[]');
      setDismissedCancelled(new Set(stored));
    } catch {}
  }, []);

  // Загружаем локальные напоминания из localStorage
  useEffect(() => {
    const loadReminders = () => {
      try {
        const data = JSON.parse(localStorage.getItem('edu_pulse_reminders') || '[]');
        setLocalReminders(data);
      } catch {}
    };
    loadReminders();
    window.addEventListener('storage', loadReminders);
    return () => window.removeEventListener('storage', loadReminders);
  }, []);

  // Load courses for quick action modals
  useEffect(() => {
    api.getCourses().then(d => setCourses(d.courses || [])).catch(() => {});
  }, []);

  // Load groups when course is selected
  useEffect(() => {
    if (newStudentForm.course_id) {
      api.getCourseGroups(newStudentForm.course_id)
        .then(d => setCourseGroups(d.groups || []))
        .catch(() => setCourseGroups([]));
    } else {
      setCourseGroups([]);
    }
  }, [newStudentForm.course_id]);

  const openNewStudentModal = () => {
    setNewStudentForm({ first_name: '', last_name: '', phone: '', course_id: '', group_id: '' });
    setShowNewStudent(true);
  };

  const openOneOffLessonModal = () => {
    const d = new Date();
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setOneOffLessonForm({ title: '', date: dateStr, start_time: '', end_time: '', location: '', location_link: '' });
    setShowOneOffLesson(true);
  };

  const handleCreateStudent = async () => {
    if (!newStudentForm.first_name.trim()) return;
    setSavingQuickAction(true);
    try {
      // Create student
      const created = await api.createStudent({
        first_name: newStudentForm.first_name,
        last_name: newStudentForm.last_name || undefined,
        phone: newStudentForm.phone || undefined,
        course_ids: '',
      });

      // If course and group selected, add to group
      if (newStudentForm.group_id) {
        await api.addStudentToGroup(newStudentForm.group_id, created.id).catch(() => {});
      } else if (newStudentForm.course_id) {
        // Fallback: enroll in course
        await api.enrollStudent(newStudentForm.course_id, created.id).catch(() => {});
      }

      setShowNewStudent(false);
      setNewStudentForm({ first_name: '', last_name: '', phone: '', course_id: '', group_id: '' });
    } catch (e) {
      console.error('Failed to create student:', e);
      alert(apiErrorMessage(e, 'Ошибка при создании ученика'));
    } finally {
      setSavingQuickAction(false);
    }
  };

  const handleCreateOneOffLesson = async () => {
    if (!oneOffLessonForm.date || !oneOffLessonForm.start_time || !oneOffLessonForm.title.trim()) return;
    setSavingQuickAction(true);
    try {
      await api.createLesson({
        date: oneOffLessonForm.date,
        start_time: oneOffLessonForm.start_time,
        end_time: oneOffLessonForm.end_time || undefined,
        title: oneOffLessonForm.title,
        lesson_type: 'one_time',
        status: 'scheduled',
        location: oneOffLessonForm.location || undefined,
        location_link: oneOffLessonForm.location_link || undefined,
      });
      setShowOneOffLesson(false);
      setOneOffLessonForm({ title: '', date: '', start_time: '', end_time: '', location: '', location_link: '' });
    } catch (e) {
      console.error('Failed to create one-off lesson:', e);
      alert(apiErrorMessage(e, 'Ошибка при создании разового занятия'));
    } finally {
      setSavingQuickAction(false);
    }
  };

  // ── Reschedule modal state ───────────────────────────────────────────
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleData, setRescheduleData] = useState<{
    reminderId: string;
    lessonId: string;
    courseId: string;
    title: string;
    originalDate: string;
    originalTime: string;
  } | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [savingReschedule, setSavingReschedule] = useState(false);
  const [rescheduleConflict, setRescheduleConflict] = useState<string | null>(null);

  // Load all lessons for conflict detection
  const [allLessons, setAllLessons] = useState<any[]>([]);

  useEffect(() => {
    // Pre-load lessons for conflict detection
    api.getLessons().then(d => setAllLessons(d.lessons || [])).catch(() => {});
  }, []);

  /** Проверка пересечения двух временных интервалов [start,end). */
  const timesOverlap = (s1: string, e1: string, s2: string, e2: string): boolean => {
    if (!s1 || !s2) return false;
    if (!e1 || !e2) return s1 === s2;
    return s1 < e2 && e1 > s2;
  };

  // Check for conflicts when date/time changes
  const checkConflict = (date: string, time: string) => {
    if (!date || !time || !rescheduleData) {
      setRescheduleConflict(null);
      return;
    }
    // Check all non-cancelled lessons that INTERSECT with this time slot
    const conflict = allLessons.find(l => {
      if (l.date !== date) return false;
      if (l.status === 'cancelled') return false;
      if (l.id === rescheduleData.lessonId) return false;
      const lStart = l.start_time || l.time || '';
      const lEnd = l.end_time || '';
      return timesOverlap(time, '', lStart, lEnd);
    });
    if (conflict) {
      const cStart = conflict.start_time || conflict.time || '';
      const cEnd = conflict.end_time || '';
      const conflictTime = cStart && cEnd ? `${cStart}—${cEnd}` : cStart;
      setRescheduleConflict(`⚠️ «${conflict.title || ''}» уже в этот промежуток (${conflictTime})`);
    } else {
      setRescheduleConflict(null);
    }
  };

  const openRescheduleModal = (reminder: any) => {
    setRescheduleData({
      reminderId: reminder.id,
      lessonId: reminder.lesson_id || '',
      courseId: reminder.course_id || '',
      title: reminder.title || 'Занятие',
      originalDate: reminder.original_date || '',
      originalTime: reminder.time || '',
    });
    // Default: today, same time as original
    const d = new Date();
    setRescheduleDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    setRescheduleTime(reminder.time || '');
    setRescheduleConflict(null);
    setShowRescheduleModal(true);
  };

  const handleRescheduleConfirm = async () => {
    if (!rescheduleData || !rescheduleDate) return;
    setSavingReschedule(true);
    try {
      // 1. Create a new make-up lesson
      const newLesson = await api.createLesson({
        course_id: rescheduleData.courseId || undefined,
        date: rescheduleDate,
        time: rescheduleTime || undefined,
        title: `Отработка: ${rescheduleData.title}`,
        lesson_type: 'make_up',
        status: 'scheduled',
        note: `Отработка отменённого занятия от ${rescheduleData.originalDate}`,
      });

      // 2. Update the original cancelled lesson's rescheduled_to
      if (rescheduleData.lessonId) {
        try {
          await api.updateLesson(rescheduleData.lessonId, {
            rescheduled_to: newLesson.id,
          });
        } catch (e) {
          console.error('Failed to update original lesson:', e);
        }
      }

      // 3. Dismiss reminder
      dismissReminder(rescheduleData.reminderId);
      dismissCancelledLesson(`cancelled_${rescheduleData.lessonId}`);

      setShowRescheduleModal(false);
      setRescheduleData(null);
    } catch (e) {
      console.error('Failed to reschedule:', e);
      alert(apiErrorMessage(e, 'Ошибка при создании отработки'));
    } finally {
      setSavingReschedule(false);
    }
  };

  // Удалить напоминание
  const dismissReminder = (id: string) => {
    const updated = localReminders.filter(r => r.id !== id);
    setLocalReminders(updated);
    localStorage.setItem('edu_pulse_reminders', JSON.stringify(updated));
  };

  // Отклонить отменённое занятие (убрать из inbox)
  const dismissCancelledLesson = (id: string) => {
    const updated = new Set(dismissedCancelled);
    updated.add(id);
    setDismissedCancelled(updated);
    localStorage.setItem('edu_pulse_dismissed_cancelled', JSON.stringify([...updated]));
  };

  const loadInbox = async () => {
    try {
      setLoading(true);
      const [data, sResult, pResult] = await Promise.all([
        api.getInbox(),
        api.getStudents().catch(() => ({ students: [] })),
        api.getPayments().catch(() => ({ payments: [] })),
      ]);

      const allStudents = sResult.students || [];
      const allPayments = pResult.payments || [];
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const enriched = { ...data, groups: [...data.groups] };

      // ── Ensure payments group exists ──────────────────────────────────
      const hasPaymentsGroup = enriched.groups.some(g => g.key === 'payments');
      if (!hasPaymentsGroup) {
        const paymentItems: InboxItem[] = [];
        for (const s of allStudents) {
          if (s.is_active !== 'true') continue;
          const name = `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.id;
          const studentPayments = allPayments.filter(p => p.student_id === s.id);

          if (studentPayments.length === 0) {
            paymentItems.push({
              id: `payment_no_${s.id}`,
              title: name,
              subtitle: 'Нет оплат · проверьте',
              priority: 'high',
              action_label: 'Добавить платеж',
              action_url: `/student/${s.id}`,
              student_id: s.id,
            });
          } else {
            const sorted = [...studentPayments].sort((a, b) => b.payment_date.localeCompare(a.payment_date));
            const lastDate = sorted[0].payment_date;
            if (lastDate < thirtyDaysAgo) {
              paymentItems.push({
                id: `payment_overdue_${s.id}`,
                title: name,
                subtitle: `Просрочена · последний платёж ${lastDate}`,
                priority: 'high',
                action_label: 'Добавить платеж',
                action_url: `/student/${s.id}`,
                student_id: s.id,
              });
            } else if (lastDate < new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]) {
              const daysSince = Math.floor((Date.now() - new Date(lastDate).getTime()) / (24 * 60 * 60 * 1000));
              paymentItems.push({
                id: `payment_soon_${s.id}`,
                title: name,
                subtitle: `Скоро · ${daysSince} дней без оплаты`,
                priority: 'low',
                action_label: 'Проверить',
                action_url: `/student/${s.id}`,
                student_id: s.id,
              });
            }
          }
        }
      // Всегда добавляем группу Оплаты (даже пустую) — вставляем после уроков
      const lessonsIdx = enriched.groups.findIndex(g => g.key === 'lessons');
      enriched.groups.splice(lessonsIdx + 1, 0, {
        key: 'payments',
        label: 'Оплаты',
        icon: '💰',
        items: paymentItems,
      });
      }

      // ── Ensure trials group exists ────────────────────────────────────
      // Показываем активных учеников без единой оплаты — они считаются пробными
      const hasTrialsGroup = enriched.groups.some(g => g.key === 'trials');
      if (!hasTrialsGroup) {
        const trialItems: InboxItem[] = [];
        const studentsWithPayments = new Set(allPayments.map(p => p.student_id));
        for (const s of allStudents) {
          if (s.is_active !== 'true') continue;
          if (studentsWithPayments.has(s.id)) continue;
          const name = `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.id;
          trialItems.push({
            id: `trial_${s.id}`,
            title: name,
            subtitle: 'Новый ученик · пробное',
            priority: 'medium',
            action_label: 'Открыть карточку',
            action_url: `/student/${s.id}`,
            student_id: s.id,
          });
        }
      // Всегда добавляем группу Пробные (даже пустую) — вставляем после Оплат
      const paymentsIdx = enriched.groups.findIndex(g => g.key === 'payments');
      enriched.groups.splice(paymentsIdx + 1, 0, {
        key: 'trials',
        label: 'Пробные',
        icon: '🌟',
        items: trialItems,
      });
      }

      // ── Recalculate stats ──────────────────────────────────────────────
      const allItems = enriched.groups.flatMap(g => g.items);
      enriched.stats = {
        total: allItems.length,
        high_priority: allItems.filter(i => i.priority === 'high').length,
      };

      setInbox(enriched);
      setError(null);
    } catch (e) {
      console.error('Failed to load inbox:', e);
      setError('Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Loading ──────────────────────────────────────────────────────────
  if (loading && !inbox) {
    return (
      <div className="p-4 space-y-4 animate-fade-in">
        <div className="skeleton h-8 w-40 mb-1" />
        <div className="skeleton h-5 w-56 mb-4" />
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="skeleton h-28 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  if (error && !inbox) {
    return (
      <div className="p-4 animate-fade-in">
        <div className="tg-card flex flex-col items-center py-8">
          <IconCloseX className="w-12 h-12 mb-3 text-[var(--tg-theme-hint-color)]" />
          <p className="text-sm text-[var(--tg-theme-hint-color)] mb-4">{error}</p>
          <button onClick={loadInbox} className="tg-button text-sm">
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (!inbox) return null;

  const totalHigh = inbox.stats.high_priority;

  return (
    <div className="p-4 space-y-3 animate-fade-in pb-6">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-1">
        <div>            <h1 className="text-2xl font-bold text-[var(--tg-theme-text-color)] flex items-center gap-2">
            <img src="/icons/today(Inbox-page)-1.svg" alt="" className="w-6 h-6" /> Сегодня
          </h1>
          <p className="text-xs text-[var(--tg-theme-hint-color)] mt-0.5">
            {formatWeekday(inbox.date)}, {inbox.date}
            {totalHigh > 0 && (
              <span className="text-red-500 font-medium ml-1">
                · {totalHigh} {totalHigh === 1 ? 'требует' : 'требуют'} внимания
              </span>
            )}
          </p>
        </div>
        <button
          onClick={loadInbox}
          className="w-9 h-9 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center hover:opacity-70 transition-opacity text-sm"
          title="Обновить"
        >
          <img src={theme === 'dark' ? '/icons/refresh_dark-theme.svg' : '/icons/refresh_light-theme.svg'} alt="" className="w-6 h-6" />
        </button>
      </div>

      {/* ── Quick summary bar ────────────────────────────────────────── */}
      <div className="flex items-center gap-2 text-[11px] text-[var(--tg-theme-hint-color)] bg-[var(--tg-theme-secondary-bg-color)] rounded-xl px-3 py-2">
        {inbox.groups.map((g, i) => (
          <span key={g.key} className="flex items-center gap-1">
            <span>{renderSummaryIcon(g.icon, 'w-3.5 h-3.5')}</span>
            <span className="font-medium">{g.items.length}</span>
            {i < inbox.groups.length - 1 && <span className="mx-0.5 text-[var(--tg-theme-section-separator-color)]">|</span>}
          </span>
        ))}
        <span className="ml-auto text-[10px] opacity-60">{inbox.stats.total} всего</span>
      </div>

      {/* ── Quick actions ───────────────────────────────────────────── */}
      <div className="flex gap-2">
        {permissions.canEditStudents && (
          <button
            onClick={openNewStudentModal}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-all active:scale-[0.98]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Новый ученик
          </button>
        )}
        {permissions.canManageLessons && (
          <button
            onClick={openOneOffLessonModal}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-all active:scale-[0.98]"
          >
            <IconTrialStar className="w-3.5 h-3.5" /> Разовое занятие
          </button>
        )}
      </div>

      {/* ── Local reminders (cancelled lessons marked later) ────────── */}
      {localReminders.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 px-1">
            <IconClockLate className="w-4 h-4 text-purple-600" />
            <span className="text-xs font-semibold text-purple-700">
              Напомнить о переносе
            </span>
            <span className="text-[10px] bg-purple-100 text-purple-600 rounded-full px-2 py-0.5 font-medium">
              {localReminders.length}
            </span>
          </div>
          {localReminders.map((reminder) => (
            <div key={reminder.id}
              className="rounded-xl border-2 border-purple-200 bg-purple-50 p-3.5 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start gap-3">
                <span className="shrink-0"><IconCloseX className="w-5 h-5 text-red-500" /></span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-purple-800 truncate">
                      {reminder.title}
                    </span>
                    <span className="text-[9px] font-bold text-purple-500 bg-purple-100 px-1.5 py-0.5 rounded-full shrink-0">
                      ВАЖНО
                    </span>
                  </div>
                  <p className="text-[11px] text-purple-600 mt-0.5">
                    Отменённое занятие от {reminder.original_date}. Нужно перенести!
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => openRescheduleModal(reminder)}
                      className="text-[11px] font-medium text-white bg-purple-500 rounded-lg px-3 py-1.5 hover:bg-purple-600 transition-colors"
                    >
                      Запланировать
                    </button>
                    {/* Кнопка "Готово" удалена — только перенос */}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Groups ───────────────────────────────────────────────────── */}
      <div className="space-y-3">
        {inbox.groups.map((group) => {
          const isCollapsed = collapsedGroups[group.key] ?? false;
          const isLessonGroup = group.key === 'lessons';

          // Pre-filter dismissed cancelled items
          const displayItems = group.key === 'cancelled'
            ? group.items.filter(i => !dismissedCancelled.has(i.id))
            : group.items;

          const hasFilteredNonLessonItems = group.key === 'cancelled' && displayItems.length === 0 && group.items.length > 0;

          return (
            <div key={group.key} className="space-y-1.5">
              <GroupHeader
                group={{ ...group, items: displayItems }}
                collapsed={isCollapsed}
                onToggle={() => toggleGroup(group.key)}
              />

              {!isCollapsed && (
                <div className="space-y-1.5 pl-2 animate-slide-up">
                  {displayItems.length === 0 && isLessonGroup ? (
                    <div className="rounded-xl bg-white border border-dashed border-gray-200 p-6 text-center">
                      <div className="mb-2"><img src="/icons/сегодня-занятий-неет.svg" alt="" className="w-20 h-20 mx-auto" /></div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Сегодня занятий нет</p>
                      <p className="text-[11px] text-gray-400">Следующее занятие появится здесь автоматически.</p>
                    </div>
                  ) : displayItems.length === 0 ? (
                    <div className="rounded-xl bg-white border border-dashed border-gray-200 p-4 text-center">
                      <p className="text-xs text-gray-500 flex items-center gap-1 justify-center"><IconPresentCheck className="w-3 h-3 text-green-500" /> Всё в порядке</p>
                    </div>
                  ) : (
                    displayItems.map((item) => {
                      if (group.key === 'lessons') {
                        return <LessonCard key={item.id} item={item} />;
                      }
                      if (group.key === 'payments') {
                        return <PaymentCard key={item.id} item={item} />;
                      }
                      if (group.key === 'trials') {
                        return <TrialCard key={item.id} item={item} />;
                      }
                      return <InboxCard key={item.id} item={item}
                        onDismiss={group.key === 'cancelled' ? dismissCancelledLesson : undefined}
                      />;
                    })
                  )}
                  {/* Если после фильтрации осталось 0 — показываем "всё готово" */}
                  {hasFilteredNonLessonItems && (
                    <div className="rounded-xl bg-green-50 border border-green-100 p-3 text-center">
                      <p className="text-xs text-green-700 flex items-center gap-1 justify-center"><IconPresentCheck className="w-3 h-3 text-green-500" /> Все отменённые занятия обработаны</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Empty state ──────────────────────────────────────────────── */}
      {inbox.groups.length === 0 && (
        <div className="tg-card flex flex-col items-center py-10 text-center">
          <span className="text-5xl mb-4">🎉</span>
          <p className="text-base font-semibold text-[var(--tg-theme-text-color)] mb-1">
            Всё отлично!
          </p>
          <p className="text-sm text-[var(--tg-theme-hint-color)]">
            Сегодня нет задач, требующих внимания.
            Можно отдохнуть 🧘
          </p>
        </div>
      )}

      {/* ── Auto-refresh hint ────────────────────────────────────────── */}
      <p className="text-center text-[9px] text-[var(--tg-theme-hint-color)] pt-1">
        Данные обновляются автоматически · {inbox.stats.total} сигналов
      </p>

      {/* ── New Student Modal ────────────────────────────────────────── */}
      {showNewStudent && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3 animate-fade-in"
          onClick={() => setShowNewStudent(false)}>
          <div className="bg-[var(--tg-theme-bg-color)] rounded-3xl w-full max-w-sm p-5 shadow-2xl animate-slide-up"
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-[var(--tg-theme-text-color)] flex items-center gap-2"><IconGroup className="w-5 h-5" /> Новый ученик</h3>
              <button onClick={() => setShowNewStudent(false)}
                className="w-8 h-8 rounded-full bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center text-sm hover:opacity-70 transition-opacity">✕</button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1 block">Имя *</label>
                  <input value={newStudentForm.first_name}
                    onChange={e => setNewStudentForm(f => ({ ...f, first_name: e.target.value }))}
                    placeholder="Имя"
                    className="w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1 block">Фамилия</label>
                  <input value={newStudentForm.last_name}
                    onChange={e => setNewStudentForm(f => ({ ...f, last_name: e.target.value }))}
                    placeholder="Фамилия"
                    className="w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1 block">📞 Телефон</label>
                <input value={newStudentForm.phone}
                  onChange={e => setNewStudentForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+7 (777) 123-45-67"
                  className="w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
              </div>

              {/* Course selector */}
              <div>
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block flex items-center gap-1"><IconBook className="w-3 h-3" /> Курс</label>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => setNewStudentForm(f => ({ ...f, course_id: '', group_id: '' }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      !newStudentForm.course_id
                        ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)] text-white'
                        : 'border-gray-200 bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                    }`}>
                    ➖ Не выбран
                  </button>
                  {courses.map(c => (
                    <button key={c.id}
                      onClick={() => setNewStudentForm(f => ({ ...f, course_id: c.id, group_id: '' }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        newStudentForm.course_id === c.id
                          ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)] text-white'
                          : 'border-gray-200 bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                      }`}>
                      {c.title}
                    </button>
                  ))}
                </div>
              </div>

              {/* Group selector (shown when course selected) */}
              {newStudentForm.course_id && courseGroups.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block flex items-center gap-1"><IconSchool className="w-3 h-3" /> Группа</label>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setNewStudentForm(f => ({ ...f, group_id: '' }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        !newStudentForm.group_id
                          ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)] text-white'
                          : 'border-gray-200 bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                      }`}>
                      ➖ Только курс
                    </button>
                    {courseGroups.map(g => (
                      <button key={g.id}
                        onClick={() => setNewStudentForm(f => ({ ...f, group_id: g.id }))}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          newStudentForm.group_id === g.id
                            ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)] text-white'
                            : 'border-gray-200 bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                        }`}>
                        {g.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowNewStudent(false)}
                className="tg-button-secondary flex-1 text-sm">Отмена</button>
              <button onClick={handleCreateStudent}
                disabled={savingQuickAction || !newStudentForm.first_name.trim()}
                className="tg-button flex-1 text-sm disabled:opacity-50">
                {savingQuickAction ? '...' : '✓ Добавить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── One-off Lesson Modal ───────────────────────────────────────── */}
      {showOneOffLesson && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-3 animate-fade-in overflow-y-auto"
          onClick={() => setShowOneOffLesson(false)}>
          <div className="bg-[var(--tg-theme-bg-color)] rounded-3xl w-full max-w-sm p-5 shadow-2xl animate-slide-up my-8"
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-[var(--tg-theme-text-color)] flex items-center gap-2"><IconTrialStar className="w-5 h-5" /> Разовое занятие</h3>
              <button onClick={() => setShowOneOffLesson(false)}
                className="w-8 h-8 rounded-full bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center text-sm hover:opacity-70 transition-opacity">✕</button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1 block flex items-center gap-1"><IconHomework className="w-3 h-3" /> Название *</label>
                <input value={oneOffLessonForm.title}
                  onChange={e => setOneOffLessonForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Мастер-класс по робототехнике"
                  className="w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
              </div>

              <div>
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1 block flex items-center gap-1"><IconCalendar className="w-3 h-3" /> Дата *</label>
                <input type="date" value={oneOffLessonForm.date}
                  onChange={e => setOneOffLessonForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1 block">🕐 Начало *</label>
                  <input type="time" value={oneOffLessonForm.start_time}
                    onChange={e => setOneOffLessonForm(f => ({ ...f, start_time: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1 block flex items-center gap-1"><IconClockLate className="w-3 h-3" /> Конец</label>
                  <input type="time" value={oneOffLessonForm.end_time}
                    onChange={e => setOneOffLessonForm(f => ({ ...f, end_time: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1 block flex items-center gap-1"><IconLocationPin className="w-3 h-3" /> Место</label>
                <input value={oneOffLessonForm.location}
                  onChange={e => setOneOffLessonForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="ул. Московская, д. 10"
                  className="w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
              </div>

              <div>
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1 block flex items-center gap-1"><IconLink className="w-3 h-3" /> Ссылка</label>
                <input value={oneOffLessonForm.location_link}
                  onChange={e => setOneOffLessonForm(f => ({ ...f, location_link: e.target.value }))}
                  placeholder="https://yandex.ru/maps/..."
                  className="w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
              </div>
            </div>

            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowOneOffLesson(false)}
                className="tg-button-secondary flex-1 text-sm">Отмена</button>
              <button onClick={handleCreateOneOffLesson}
                disabled={savingQuickAction || !oneOffLessonForm.date || !oneOffLessonForm.start_time || !oneOffLessonForm.title.trim()}
                className="tg-button flex-1 text-sm disabled:opacity-50">
                {savingQuickAction ? '...' : '✓ Создать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reschedule Modal ──────────────────────────────────────────── */}
      {showRescheduleModal && rescheduleData && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => { setShowRescheduleModal(false); setRescheduleData(null); }}>
          <div className="w-full max-w-sm bg-[var(--tg-theme-bg-color)] rounded-3xl p-5 shadow-2xl animate-slide-up"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold flex items-center gap-2"><IconCalendar className="w-4 h-4" /> Запланировать отработку</h3>
              <button onClick={() => { setShowRescheduleModal(false); setRescheduleData(null); }}
                className="p-1 text-[var(--tg-theme-hint-color)]">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            <div className="mb-3 p-3 rounded-xl bg-purple-50 border border-purple-100">
              <p className="text-xs font-semibold text-purple-800 flex items-center gap-1"><IconCloseX className="w-3 h-3 text-red-500" /> {rescheduleData.title}</p>
              <p className="text-[10px] text-purple-600 mt-0.5">
                Отменено {rescheduleData.originalDate}
                {rescheduleData.originalTime && ` в ${rescheduleData.originalTime}`}
              </p>
            </div>

            {/* Date picker */}
            <div className="mb-3">
              <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1 block">📆 Дата отработки *</label>
              <input type="date" value={rescheduleDate}
                onChange={e => {
                  setRescheduleDate(e.target.value);
                  checkConflict(e.target.value, rescheduleTime);
                }}
                className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
            </div>

            {/* Time picker */}
            <div className="mb-4">
              <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1 block flex items-center gap-1"><IconClockLate className="w-3 h-3" /> Время</label>
              <input type="time" value={rescheduleTime}
                onChange={e => {
                  setRescheduleTime(e.target.value);
                  checkConflict(rescheduleDate, e.target.value);
                }}
                className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
            </div>

            {/* Conflict warning */}
            {rescheduleConflict && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-2">
                <span>⚠️</span>
                <span>{rescheduleConflict}</span>
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={() => { setShowRescheduleModal(false); setRescheduleData(null); }}
                className="tg-button-secondary flex-1 text-sm">Отмена</button>
              <button onClick={handleRescheduleConfirm}
                disabled={savingReschedule || !rescheduleDate || !!rescheduleConflict}
                className="tg-button flex-1 text-sm disabled:opacity-50">
                {savingReschedule ? '...' : '✓ Запланировать'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
